import { useState, useRef, useEffect } from 'react';
import { Outlet, Link, NavLink, useNavigate, useLocation } from 'react-router';
import { BookOpen, PenTool, Sparkles } from './icons';
import { OfflineIndicator } from '../shared/components/OfflineIndicator';
import { ChangelogPopup } from '../shared/components/ChangelogPopup';
import { NotificationBell } from '../shared/components/NotificationBell';
import { useAuthStore } from '../shared/stores/authStore';
import { useMyProfile } from '../shared/hooks/useProfileCrud';
import { ThemeToggle } from '../shared/components/ThemeProvider';
import { AnimatePresence, PageTransition } from '../shared/components/motion';

/* ── User dropdown menu ─────────────────────────────────────── */
function UserMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const navigate = useNavigate();
  const { data: profile } = useMyProfile();

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const displayName = profile?.username ?? user?.user_metadata?.username ?? '用户';
  const avatarUrl = profile?.avatar_url;
  const initial = displayName.charAt(0)?.toUpperCase() ?? 'U';

  return (
    <div className="relative" ref={menuRef}>
      <button type="button"
        onClick={() => setOpen(!open)}
        className="flex h-8 w-8 items-center justify-center rounded-full overflow-hidden bg-brand-100 text-brand-700 text-sm font-semibold hover:ring-2 hover:ring-brand-300 transition-all"
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt={displayName} className="h-full w-full object-cover" />
        ) : (
          initial
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-border bg-card shadow-xl py-1 z-50">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-medium truncate">{displayName}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <Link
            to={`/user/${user?.id}`}
            onClick={() => setOpen(false)}
            className="block w-full text-left px-4 py-2.5 text-sm hover:bg-secondary transition-colors"
          >
            👤 个人主页
          </Link>
          <Link
            to="/settings/profile"
            onClick={() => setOpen(false)}
            className="block w-full text-left px-4 py-2.5 text-sm hover:bg-secondary transition-colors"
          >
            ⚙️ 个人资料设置
          </Link>
          <div className="border-t border-border mt-1" />
          <button type="button"
            onClick={async () => { setOpen(false); try { await signOut(); } catch { /* signOut best-effort */ } navigate('/'); }}
            className="w-full text-left px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
          >
            退出登录
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Nav link style helper ───────────────────────────────────── */
const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
    isActive
      ? 'bg-brand-100 text-brand-700'
      : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
  }`;

/* ── Continue Writing shortcut (BD-07) ─────────────────────── */
function ContinueWritingButton() {
  const [target, setTarget] = useState<{ novelId: string; title: string } | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('novelforge-last-edit');
      if (raw) {
        const parsed = JSON.parse(raw) as { novelId?: string; title?: string };
        if (parsed.novelId) setTarget({ novelId: parsed.novelId, title: parsed.title || '未命名作品' });
      }
    } catch { /* ignore */ }
  }, []);

  if (!target) return null;
  return (
    <Link
      to={`/studio/${target.novelId}/writing`}
      className="hidden lg:flex items-center gap-1.5 rounded-lg bg-brand-50 border border-brand-200 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-100 transition-colors"
      title={`继续写作: ${target.title}`}
    >
      <PenTool className="h-3.5 w-3.5" />
      <span className="max-w-[100px] truncate">继续写作</span>
    </Link>
  );
}

/* ── Root Layout ────────────────────────────────────────────── */
export function RootLayout() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
      {/* Skip navigation — keyboard-only, visible on :focus */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg"
      >
        跳转到主要内容
      </a>

      {/* ── Top Navigation Bar ────────────────────────────── */}
      <header className="relative z-30 flex h-14 shrink-0 items-center border-b border-border bg-card/80 backdrop-blur-lg px-4 md:px-6" role="banner">
        {/* Left: Logo + Nav */}
        <div className="flex items-center gap-5">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-brand-600 to-brand-800 flex items-center justify-center text-white font-bold text-sm shadow-md">
              N
            </div>
            <span className="hidden sm:block text-base font-bold tracking-tight font-serif">诺文</span>
          </Link>

          {/* Separator */}
          <div className="hidden md:block h-5 w-px bg-border" />

          {/* Main nav */}
          <nav aria-label="主导航" className="hidden md:flex items-center gap-1">
            <NavLink to="/plaza" end className={navLinkClass}>
              <BookOpen className="h-4 w-4" />
              阅读广场
            </NavLink>
            <NavLink to="/bookshelf" className={navLinkClass}>
              <BookOpen className="h-4 w-4" />
              书架
            </NavLink>
            <NavLink to="/workshop" className={navLinkClass}>
              <PenTool className="h-4 w-4" />
              创作中心
            </NavLink>
            <NavLink to="/arena" className={(props) => {
              const base = navLinkClass(props);
              return `${base} arena-nav-glow`;
            }}>
              <Sparkles className="h-4 w-4" />
              共创竞技场
            </NavLink>
            <NavLink to="/derivatives" className={navLinkClass}>
              <BookOpen className="h-4 w-4" />
              同人衍生
            </NavLink>
            <NavLink to="/community" className={navLinkClass}>
              <PenTool className="h-4 w-4" />
              社区
            </NavLink>
          </nav>
        </div>

        {/* Right: Actions */}
        <div className="ml-auto flex items-center gap-3">
          <ThemeToggle />
          {user ? (
            <>
              <ContinueWritingButton />
              <NotificationBell />
              <UserMenu />
            </>
          ) : (
            <>
              <Link to="/auth/login"
                className="hidden md:block rounded-lg px-3.5 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                登录
              </Link>
              <Link to="/auth/register"
                className="hidden md:block rounded-lg bg-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
                免费注册
              </Link>
            </>
          )}

          {/* Mobile nav (hamburger) */}
          <MobileNav />
        </div>
      </header>

      {/* ── Main Content (animated route transitions — AM-01) ── */}
      <main id="main-content" className="flex-1 overflow-auto scroll-smooth" role="main">
        <AnimatePresence mode="wait">
          <PageTransition key={location.pathname}>
            <Outlet />
          </PageTransition>
        </AnimatePresence>
      </main>

      {/* ── Footer bar — version + offline indicator ─── */}
      <footer className="flex items-center justify-between px-4 py-1 text-xs text-muted-foreground border-t border-border/50 bg-card/50 shrink-0" role="contentinfo">
        <ChangelogPopup />
        <span className="hidden sm:block">诺文 NovelForge</span>
      </footer>

      {/* ── Fixed overlays ────────────────────────────────── */}
      <OfflineIndicator />
    </div>
  );
}

/* ── Mobile hamburger nav ────────────────────────────────────── */
function MobileNav() {
  const [open, setOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const navigate = useNavigate();

  return (
    <>
      <button type="button" onClick={() => setOpen(!open)} aria-label={open ? '关闭菜单' : '打开菜单'} className="flex md:hidden h-9 w-9 items-center justify-center rounded-lg hover:bg-secondary transition-colors">
        <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          {open ? <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></> : <><path d="M4 8h16" /><path d="M4 16h16" /></>}
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full border-b border-border bg-card md:hidden z-40">
          <nav aria-label="移动端导航" className="p-4 space-y-1">
            <Link to="/plaza" onClick={() => setOpen(false)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
              <BookOpen className="h-4 w-4" /> 阅读广场
            </Link>
            <Link to="/bookshelf" onClick={() => setOpen(false)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
              <BookOpen className="h-4 w-4" /> 书架
            </Link>
            <Link to="/workshop" onClick={() => setOpen(false)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
              <PenTool className="h-4 w-4" /> 创作中心
            </Link>
            <Link to="/arena" onClick={() => setOpen(false)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors arena-nav-glow">
              <Sparkles className="h-4 w-4" /> 共创竞技场
            </Link>
            <Link to="/derivatives" onClick={() => setOpen(false)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
              <BookOpen className="h-4 w-4" /> 同人衍生
            </Link>
            <Link to="/community" onClick={() => setOpen(false)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
              <PenTool className="h-4 w-4" /> 社区
            </Link>
          </nav>
          <div className="border-t border-border p-4">
            {user ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">{user.user_metadata?.username ?? user.email}</p>
                <div className="flex gap-3">
                  <Link to={`/user/${user.id}`} onClick={() => setOpen(false)}
                    className="text-sm text-primary hover:underline">个人主页</Link>
                  <Link to="/settings/profile" onClick={() => setOpen(false)}
                    className="text-sm text-primary hover:underline">资料设置</Link>
                </div>
                <button type="button"
                  onClick={async () => { setOpen(false); try { await signOut(); } catch { /* signOut best-effort */ } navigate('/'); }}
                  className="text-sm text-destructive hover:underline"
                >
                  退出登录
                </button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Link to="/auth/login" onClick={() => setOpen(false)}
                  className="flex-1 rounded-lg border border-border py-2 text-sm font-medium hover:bg-secondary transition-colors text-center">
                  登录
                </Link>
                <Link to="/auth/register" onClick={() => setOpen(false)}
                  className="flex-1 rounded-lg bg-primary py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors text-center">
                  注册
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
